<template>
    <component :is="tableOnly ? 'div' : 'app-content'" class="module-time-tracker evaluation-view" v-bind="appContentProperties">
        <template #default>
            <div v-if="errorMessage.default" class="default">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div v-html="errorMessage.default"></div>
            </div>
            <div v-else-if="errorMessage.error" class="error">
                <i class="material-icons md-light" aria-hidden="true" role="img">clear</i>
                <div v-html="errorMessage.error"></div>
            </div>

            <div v-if="!tableOnly" class="flex margin-bottom-15">
                <router-link v-if="isManualMode" :to="{name: 'time-tracker-evaluation-edit', params: {employee, week}}" class="button button-primary margin-right-15">
                    <i class="material-icons" aria-hidden="true" role="img">add</i>
                    <span>Hinzufügen</span>
                </router-link>
                <a :href="`/employee/time-tracker/evaluation/generate-week/employee/${employee}/week/${week}`" target="_blank" class="button button-primary">
                    <i class="material-icons" aria-hidden="true" role="img">picture_as_pdf</i>
                    <span>PDF erstellen</span>
                </a>
                <span class="flex-grow"></span>
                <app-popover :close-on-scroll="false" position="bottom" wrapper-element="span" class="margin-right-15">
                    <template #default="{toggle, close, open}">
                        <button v-interact="() => toggle()" class="button button-default" type="button">
                            <i class="material-icons md-16" aria-hidden="true" role="img">info</i>
                            <span><span class="visible-md-inline-upward">Informationen zum </span>Arbeitsverhältnis</span>
                        </button>
                    </template>
                    <template #container>
                        <div style="max-width: 400px;">
                            <div class="infobox infobox-border">
                                <div class="infobox-headline">
                                    <strong>Arbeitsverhältnis</strong>
                                </div>

                                <template v-for="(workStateEntry, index) in workTimeWidget.work_state_history">
                                    <hr v-if="index > 0" class="small">
                                    <div class="row">
                                        <div class="col-sm-6">
                                            <strong>Vertragsabschluss</strong>
                                            <div>{{workStateEntry.contract_date ? dateHelper('d.m.Y', workStateEntry.contract_date) : 'Keine Angabe'}}</div>
                                            <div class="spacer spacer-5"/>
                                        </div>

                                        <div class="col-sm-6">
                                            <strong>Arbeitsbeginn</strong>
                                            <div>{{workStateEntry.since ? dateHelper('d.m.Y', workStateEntry.since) : 'Keine Angabe'}}</div>
                                            <div class="spacer spacer-5"/>
                                        </div>

                                        <div class="col-sm-6" v-bind="insightAttributes" v-tooltip>
                                            <strong>Wochenstunden</strong>
                                            <div>
                                                {{workStateEntry.weekly_hours}}
                                            </div>
                                            <div class="spacer spacer-5"/>
                                        </div>

                                        <div class="col-sm-6" v-bind="insightAttributes" v-tooltip>
                                            <strong>Urlaubstage</strong>
                                            <div>{{workStateEntry.vacation_days}}</div>
                                            <div class="spacer spacer-5"/>
                                        </div>

                                        <div class="col-12" v-bind="insightAttributes" v-tooltip>
                                            <strong>Arbeitszeitmodell</strong>
                                            <div class="flex gap-5 flex-wrap">
                                                <div>{{workStateEntry.work_days_default ? workStateEntry.work_days_default : workStateEntry.work_days_custom}}-Tage-Woche</div>
                                                <div v-if="workStateEntry.settings_days && workStateEntry.settings_days.length">
                                                    <div v-for="weekDays in workStateEntry.settings_days">
                                                        ({{weekDays}})
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="spacer spacer-5"/>
                                        </div>
                                    </div>
                                    <hr>
                                    <div class="text-center">
                                        <router-link :to="{name: 'employee-view', params: {id: employee}}" class="button button-secondary spacer-10">
                                            Zum Mitarbeiterprofil
                                        </router-link>
                                    </div>

                                </template>
                            </div>
                        </div>
                    </template>
                </app-popover>
                <app-popover :close-on-scroll="false" position="bottom" wrapper-element="span">
                    <template #default="{toggle, close, open}">
                        <button v-interact="() => toggle()" class="button button-default" type="button">
                            <i class="material-icons md-16" aria-hidden="true" role="img">info</i><span>Legende</span>
                        </button>
                    </template>
                    <template #container>
                        <div style="max-width: 400px;">
                            <div class="infobox infobox-border">
                                <div class="infobox-headline pointer">
                                    <strong>Legende</strong>
                                </div>

                                <div class="row">
                                    <div v-for="legend in evaluationLegend" class="col-md-6">
                                        <i :class="['material-icons', legend.iconClass]" aria-hidden="true">{{legend.icon}}</i>
                                        {{legend.title}}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </app-popover>
            </div>
            <template v-if="tableOnly && 0 === days.length">
                <loading-indicator/>
            </template>
            <portuna-box v-if="!tableOnly || 0 !== days.length" class="evaluation-table-box no-padding">
                <template #title>
                    <i class="material-icons" aria-hidden="true" role="img">access_time</i>
                </template>
                <template #additionally>
                    <div class="flex flex-vertical-middle">
                        <form-select v-if="accessibleEmployeesSelectOptions.length > 1" :options="accessibleEmployeesSelectOptions" @input="goToEmployee"
                            class="kool-choiz-transparent no-print text-overflow"
                            :model-value="employee" :inherit-width="false"/>
                        <div class="flex-grow">&nbsp;</div>
                    </div>
                    <div class="flex--item-left flex flex-wrap gap-10">
                        <div v-if="isManualMode">
                            <i class="material-icons icon-fix-top md-16" aria-hidden="true" role="img">article</i>
                            <span>Manuelle Erfassung</span>
                        </div>

                        <div v-if="usedSystems.length === 1">
                            <i class="material-icons icon-fix-top md-16" aria-hidden="true" role="img">{{systemIcon[usedSystems[0]].icon}}</i>
                            <span>{{systemIcon[usedSystems[0]].tooltipCapture}}</span>
                        </div>
                    </div>
                    <div class="flex flex-vertical-middle flex--item-right">
                        <div v-if="!isToday && todayLinkParams" class="evaluation-week-range no-print">
                            <router-link :to="todayLinkParams">
                                <i class="material-icons md-18" aria-hidden="true" role="img">radio_button_checked</i>
                                <span>Heute</span>
                            </router-link>
                        </div>
                        <div class="evaluation-week-range">
                            <router-link v-if="previousLinkParams" :to="previousLinkParams" class="no-print"
                                title="7 Tage zurück">
                                <i class="material-icons" aria-hidden="true" role="img">arrow_left</i>
                            </router-link>
                            <span class="text-vertical-middle">
                                <span class="screen-reader-only">{{screenReaderRange}}</span>
                                <span aria-hidden="true">
                                    {{range}}
                                    (KW{{dateInformation.week}})
                                </span>
                            </span>
                            <router-link v-if="nextLinkParams" :to="nextLinkParams" class="no-print"
                                title="7 Tage weiter">
                                <i class="material-icons" aria-hidden="true" role="img">arrow_right</i>
                            </router-link>
                        </div>
                    </div>
                </template>
                <loading-indicator v-if="reloading"/>
                <table v-else class="portuna-table portuna-table-bordered evaluation-box-table spacer-15">
                    <colgroup>
                        <col style="width: 45px;"/>
                        <col style="width: 14%;">
                        <col style="width: 16%;">
                        <col style="width: 16%;">
                        <col v-if="isTimeClockMode" style="width: 8%;">
                        <col v-if="hasSurcharges" style="width: 16%;">
                        <col v-if="hasProject || hasContact" style="width: 30%;">
                        <col :style="{width: hasProject ? '20%' : 'auto'}">
                        <col v-if="!tableOnly" style="width: 54px;">
                    </colgroup>
                    <thead>
                    <tr>
                        <th/>
                        <th>Datum</th>
                        <th>Arbeitszeit</th>
                        <th>Pausenzeit</th>
                        <th v-if="isTimeClockMode">Kernzeit</th>
                        <th v-if="hasSurcharges">Zuschläge</th>
                        <th v-if="hasProject || hasContact">Projektzeiten</th>
                        <th>Kommentar</th>
                        <th v-if="!tableOnly"></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(day, date) in daysFiltered" class="vertical-align-top">
                        <td></td>

                        <td class="max-width-10">{{dateHelper('D, d.m.Y', date)}}</td>

                        <td>
                            <template v-if="isManualMode || date <= today || day.work_time.absence || day.work_time.absence_time">
                                <div v-if="day.work_time.string || !isManualMode || 0 !== day.data.length || day.work_time.absence" class="flex-vertical-middle">
                                    <strong v-if="day.work_time.string" v-html="day.work_time.string" :class="{'text-warning': !isWorkDay(date)}"/><br>
                                    <small v-if="day.surcharges && day.surcharges.int" v-tooltip aria-label="Arbeitszeit ohne Zeitzuschläge">
                                        ({{formatSeconds(day.work_time.int - day.surcharges.int)}})
                                    </small>
                                    <span v-if="day.work_time.has_info_popup"
                                        v-interact="() => showRequestPopup(day.work_time.id, day.work_time.type, ['time_tracker_overtime_cut', 'absence'].includes(day.work_time.type))" v-tooltip
                                        :aria-label="day.work_time.type === 'absence' ? 'Abwesenheit anzeigen' : 'Antrag anzeigen'" class="padding-right-5">
                                        <i aria-hidden="true" class="material-icons link-color" role="img">visibility</i>
                                    </span>
                                    <strong v-if="day.work_time.int && day.work_time.absence" :class="{'text-warning': allowWorkOutsideWorkTime && !isWorkDay(date)}">({{formatSeconds(day.work_time.int)}})</strong><br>
                                    <span v-if="overtimeAbsences[date]">({{overtimeAbsences[date].duration}})</span>
                                    <span v-if="'absence' === day.work_time.type && day.work_time.absence_time !== 0"
                                        v-tooltip aria-label="Wird von der Wochenarbeitszeit abgezogen" class="padding-left-5 vertical-align-middle">
                                        <img alt="Wochenarbeitszeit abgezogen" src="/img/absences_icon.svg" style="max-width: 20px;"/>
                                    </span>
                                    <span v-if="day.home_office" v-tooltip aria-label="Mobile Arbeit">
                                        <i aria-hidden="true" class="material-icons text-success font-size-20" role="img">home</i>
                                    </span>
                                </div>
                                <span v-else-if="!isWorkDay(date) && day.data.length && allowWorkOutsideWorkTime" class="text-warning flex">
                                  <i v-tooltip="'Diese Zeiten werden nicht auf Ihre wöchentliche Arbeitszeit gerechnet. Ihre Arbeitstage sind ' + workDaysList.days" class="material-icons text-warning font-size-20" aria-hidden="true" role="img">priority_high</i><strong>außerhalb der Arbeitstage</strong>
                                </span>
                                <span v-else-if="isManualMode && 0 === day.data.length">
                                    <strong v-if="week >= currentWeek || !weekEntry">keine Daten eingetragen</strong>
                                    <strong v-else>abwesend</strong>
                                </span>
                                <span v-else>
                                    <i class="material-icons" aria-hidden="true" role="img">remove</i>
                                </span>
                            </template>
                            <evaluation-attendances v-if="date <= today || isManualMode" :key="date + '_workTime'" :attendances="day.data"
                                :requested-corrections="requestedCorrections" :is-manual-mode="isManualMode" :has-multiple-systems="employeeEnabledSystems.length > 1" :settings="settings"
                               :allow-work-outside-work-time="allowWorkOutsideWorkTime"/>
                        </td>

                        <td class="vertical-align-middle" :class="{flex: !day.pause.data.length}">
                            <template v-if="date <= today">
                                <span v-if="day.work_time.int">
                                    <strong v-if="!day.break_time.status">
                                        {{formatSeconds(day.break_time.int)}}
                                    </strong>
                                    <strong v-else-if="'ok' === day.break_time.status" class="text-success">
                                        <i class="material-icons" aria-hidden="true" role="img">check</i> <span>{{formatSeconds(day.break_time.int)}}</span>
                                    </strong>
                                    <strong v-else v-tooltip="coreTimeErrors[day.break_time.status].message" :class="{'text-danger': 'error' === coreTimeErrors[day.break_time.status].type, 'text-warning': 'warning' === coreTimeErrors[day.break_time.status].type}">
                                        <i class="material-icons" aria-hidden="true" role="img">{{coreTimeErrors[day.break_time.status].icon}}</i> <span class="font-weight-500">{{formatSeconds(day.break_time.int)}}</span>
                                    </strong>
                                </span>
                                <span v-else-if="isTimeClockMode">
                                    <i class="material-icons" aria-hidden="true" role="img">remove</i>
                                </span>
                            </template>
                            <evaluation-attendances v-if="date <= today || isManualMode" :key="date + '_breakTime'" :attendances="day.pause.data"
                                :requested-corrections="requestedCorrections" is-break :is-manual-mode="isManualMode" :has-multiple-systems="employeeEnabledSystems.length > 1" :date="date" :settings="settings"
                               :allow-work-outside-work-time="allowWorkOutsideWorkTime"/>
                        </td>

                        <td v-if="isTimeClockMode">
                            <template v-if="date <= today">
                                <span v-if="day.work_time.int">
                                    <i v-if="day.core_time" class="material-icons text-success" aria-hidden="true" role="img">done</i>
                                    <i v-else class="material-icons text-danger" aria-hidden="true" role="img">clear</i>
                                </span>
                                <span v-else-if="isTimeClockMode"><i class="material-icons" aria-hidden="true" role="img">remove</i></span>
                            </template>
                        </td>

                        <td v-if="hasSurcharges">
                            <span v-tooltip="day.surcharges.info">
                               {{day.surcharges.string}}
                            </span>
                        </td>

                        <td v-if="hasProject || hasContact">
                            <evaluation-attendances v-if="date <= today || isManualMode" :key="date + '_projectTimes'" :attendances="projectTimesDate[date] ?? []"
                                :requested-corrections="requestedCorrections"
                                :is-project="hasProject" :is-contact="hasContact" :is-manual-mode="isManualMode" :has-multiple-systems="employeeEnabledSystems.length > 1" :settings="settings"
                                :allow-work-outside-work-time="allowWorkOutsideWorkTime"/>
                        </td>

                        <td>
                            <div v-if="day.note" v-html="day.note.note" class="message-content fr-view"/>
                        </td>

                        <td v-if="!tableOnly">
                            <span v-interact="() => editDayNote(date, day)" :aria-label="day.note ? 'Kommentar bearbeiten' : 'Kommentar hinzufügen'" class="icon-interact">
                                <i :title="day.note ? 'Kommentar bearbeiten' : 'Kommentar hinzufügen'" class="material-icons text-info font-size-16" aria-hidden="true" role="img">subject</i>
                            </span>

                            <template v-if="hasMode">
                                <span v-if="!freezeEnd && day.editable && (date < today || isManualMode || (hasProject || hasContact) && date === today)" v-interact="() => checkRequestCorrection(date, employee)"
                                    :aria-label="editIconLabel" class="icon-interact">
                                    <i v-if="allowWorkOutsideWorkTime && !isWorkDay(date)" :title="editIconLabel" class="material-icons text-warning font-size-16" aria-hidden="true" role="img">history_toggle_off</i>
                                    <i v-else :title="editIconLabel" class="material-icons font-size-16" :class="day.data.length? 'text-warning' : 'color-primary'" aria-hidden="true" role="img">edit</i>
                                </span>
                                <i v-else-if="day.message" :title="day.message" class="material-icons font-size-16" aria-hidden="true" role="img">edit</i>
                                <i v-else-if="date === today" title="Hier können Sie ab morgen eine Korrektur beantragen." class="material-icons font-size-16"
                                    aria-hidden="true" role="img">hourglass_empty</i>
                                <i v-else-if="date > today" title="In der Zukunft kann keine Korrektur beantragt werden." class="material-icons font-size-16"
                                    aria-hidden="true" role="img">hourglass_empty</i>
                                <i v-else-if="allowManualOvertimeFreeze && (freezeEnd && week <= freezeEnd.date)" v-interact="() => showFreezeInfoPopup(freezeEnd)"
                                    v-tooltip="'Der Wert des Stundenkontos wurde für diese Woche festgesetzt, um das Stundenkonto des Mitarbeiters vor Änderungen zu schützen. Die Auswertung für diese Woche kann daher nicht verändert werden.'"
                                    aria-hidden="true" class="material-icons pointer font-size-16" role="img">edit</i>

                            </template>
                            <template v-else-if="!hasMode && day.data.length">
                                <i title="Nur Mitarbeiter mit ausgewähltem Zeiterfassungsystem können Korrekturen beantragen." class="material-icons font-size-16"
                                    aria-hidden="true" role="img">edit</i>
                            </template>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </portuna-box>
            <template v-if="!tableOnly">
                <div class="row spacer-15">
                    <div class="col-lg-6">
                        <portuna-box class="no-padding" title="Wochenzeit und Stundenkonto" title-icon="access_time">
                            <loading-indicator v-if="reloading"/>
                            <table v-else class="portuna-table portuna-table-bordered">
                                <tbody>
                                <tr>
                                    <td>
                                        <strong>Wochenarbeitszeit:</strong>
                                    </td>
                                    <td class="text-center">
                                        <strong>{{workTimeWidget.weekly_time}}</strong>
                                    </td>
                                </tr>
                                <tr v-if="workTimeWidget.reduction_data">
                                    <td>
                                        <strong>
                                            <i class="material-icons text-warning" aria-hidden="true" role="img">{{workTimeWidget.reduction_data.icon}}</i> {{workTimeWidget.reduction_data.title}}
                                        </strong>
                                    </td>
                                    <td class="text-center">
                                        <strong v-if="workTimeWidget.reduction_data.value > 0">+{{formatSeconds(workTimeWidget.reduction_data.value)}}</strong>
                                        <strong v-else-if="workTimeWidget.reduction_data.value < 0">{{formatSeconds(workTimeWidget.reduction_data.value)}}</strong>
                                    </td>
                                </tr>
                                <template v-for="(entries, date) of workTimeWidget.absence_data">
                                    <template v-if="entries.length === 1">
                                        <tr>
                                            <td>
                                                <strong>
                                                    {{date}}
                                                    (<i aria-hidden="true" class="material-icons" role="img">{{entries[0].icon}}</i> {{entries[0].title}}
                                                    <span v-if="'vacation' === entries[0].type && entries[0].ids && entries[0].ids.length === 1"
                                                        v-interact="() => showRequestPopup(entries[0].ids[0], 'vacation')" v-tooltip
                                                        aria-label="Antrag anzeigen" class="padding-right-5">
                                                        <i aria-hidden="true" class="material-icons link-color" role="img">visibility</i>
                                                    </span>
                                                    <span v-if="'absence' === entries[0].type && entries[0].ids && entries[0].ids.length === 1"
                                                        v-interact="() => showRequestPopup(entries[0].ids[0], 'absence')" v-tooltip
                                                        aria-label="Abwesenheit anzeigen" class="padding-right-5">
                                                        <i aria-hidden="true" class="material-icons link-color" role="img">visibility</i>
                                                    </span>)
                                                </strong>
                                            </td>
                                            <td class="text-center">
                                                <strong >{{formatSeconds(entries[0].value)}}</strong>
                                            </td>
                                        </tr>
                                    </template>
                                    <template v-else>
                                        <tr>
                                            <td colspan="2">
                                                <strong>{{date}}</strong>
                                            </td>
                                        </tr>
                                        <tr >
                                            <td style="padding-left: 15px;">
                                                <strong>
                                                    └ <i aria-hidden="true" class="material-icons" role="img">{{entry.icon}}</i>
                                                    {{entry.title}}
                                                    <span v-if="'vacation' === entry.type && entry.ids && entry.ids.length === 1"
                                                        v-interact="() => showRequestPopup(entry.ids[0], 'vacation')" v-tooltip
                                                        aria-label="Antrag anzeigen" class="padding-right-5">
                                                        <i aria-hidden="true" class="material-icons link-color" role="img">visibility</i>
                                                    </span>
                                                </strong>
                                            </td>
                                            <td class="text-center">
                                                <strong>{{formatSeconds(entry.value)}}</strong>
                                            </td>
                                        </tr>
                                    </template>
                                </template>
                                <tr v-for="entry of workTimeWidget.work_data">
                                    <template v-if="entry.overtime_ids">
                                        <td :style="{borderTop: entry.border_top ? '1px solid #757575' : ''}"
                                            colspan="2">
                                            <strong>{{entry.title}}</strong>
                                            <i v-if="entry.settingsToolTip" v-tooltip="entry.settingsToolTip" class="material-icons cursor-help" aria-hidden="true" role="img">
                                                settings
                                            </i>
                                            - genehmigter Antrag
                                            <span v-if="entry.overtime_ids.length === 1"
                                                v-interact="() => showRequestPopup(entry.overtime_ids[0], 'time_tracker_overtime_cut')" v-tooltip
                                                aria-label="Antrag anzeigen" class="padding-right-5">
                                                <i aria-hidden="true" class="material-icons link-color" role="img">visibility</i>
                                            </span>
                                            bis zu:
                                            <strong>
                                                <component :is="entry.href ? 'router-link' : 'span'" v-bind="{to: entry.href ? entry.href : ''}">
                                                    {{entry.value}} <i v-if="entry.icon" class="material-icons" aria-hidden="true" role="img">{{entry.icon}}</i>
                                                </component>
                                            </strong>
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td :style="{borderTop: entry.border_top ? '1px solid #757575' : ''}">
                                            <strong>{{entry.title}}</strong>
                                            <i v-if="entry.settingsToolTip" v-tooltip="entry.settingsToolTip" class="material-icons cursor-help" aria-hidden="true" role="img">
                                                settings
                                            </i>
                                            <i v-if="entry.infoTooltip" v-tooltip="{text: entry.infoTooltip, css: {maxWidth: '600px'}}" class="material-icons cursor-help" aria-hidden="true" role="img">
                                                info
                                            </i>
                                            <span v-if="entry.lastWeekAccount">
                                                Vorwoche: {{entry.lastWeekAccount}}
                                            </span>
                                        </td>
                                        <td class="text-center" :style="{borderTop: entry.border_top ? '1px solid #757575' : '', color: entry.color ? entry.color : ''}">
                                            <strong>
                                                <component :is="entry.href ? 'router-link' : 'div'" v-bind="{to: entry.href ? entry.href : ''}">
                                                    {{entry.value}} <i v-if="entry.icon" class="material-icons" aria-hidden="true" role="img">{{entry.icon}}</i>
                                                </component>
                                            </strong>
                                        </td>
                                    </template>
                                </tr>
                                </tbody>
                            </table>
                        </portuna-box>

                        <list-pager v-bind="searchProperties" v-if="hasProject" :entries="projectTimes" #default="{listEntries, loading}" :show-pager="false" :show-entry-count="false">
                            <table class="portuna-table portuna-table-bordered portuna-table-tr-bordered default-margin-top">
                                <colgroup>
                                    <col style="width: 45px;"/>
                                    <col>
                                    <col>
                                </colgroup>

                                <thead>
                                <tr>
                                    <th class="text-center background-dark">
                                        <i class="material-icons md-20 table-icon contrast-primary-light" aria-hidden="true" role="img">work_history</i>
                                    </th>
                                    <table-search sort-field="title" v-model="search.title" text="Projekt"/>
                                    <th v-sort="'time_gross_int'">Zeit</th>
                                </tr>
                                </thead>

                                <tbody>
                                <tr v-for="project in listEntries" class="vertical-align-top" :class="{'text-danger thick-border-top thick-border-bottom': !project.id}">
                                    <td></td>

                                    <td class="text-overflow">
                                        <router-link v-if="project.id && project.id !== patrolProject" :to="{name: 'time-tracker-project-view', params: {id: project.id}}">
                                            {{project.title}}
                                        </router-link>
                                        <span v-else>{{project.title}}</span>
                                    </td>

                                    <td :title="projectTimesTotal.break.int ? formatSeconds(project.time_net.int) + ' - ' + project.break.string + ' Pause' : null">
                                        {{project.time_gross.string}} Stunden
                                        <small v-if="project.break.int" title="Automatischer Pausenabzug">(abzgl. {{project.break.string}} Pause)</small>
                                    </td>
                                </tr>

                                <tr class="thick-border-top font-weight-500">
                                    <td></td>

                                    <td>
                                        <div class="spacer-10 spacer-10-bottom">
                                            Summe Projektzeiten
                                        </div>
                                    </td>

                                    <td :title="projectTimesTotal.break.int ? formatSeconds(projectTimesTotal.time_net.int) + ' - ' + projectTimesTotal.break.string + ' Pause' : null">
                                        <div class="spacer-10 spacer-10-bottom">
                                            {{projectTimesTotal.time_gross.string}} Stunden
                                            <small v-if="projectTimesTotal.break.int" title="Automatischer Pausenabzug">(abzgl. {{projectTimesTotal.break.string}} Pause)</small>
                                        </div>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </list-pager>

                        <list-pager v-bind="searchProperties" v-if="hasContact" :entries="contactTimes" #default="{listEntries, loading}" :show-pager="false"
                            :show-entry-count="false">
                            <table class="portuna-table portuna-table-bordered portuna-table-tr-bordered default-margin-top default-margin-bottom">
                                <colgroup>
                                    <col style="width: 45px;"/>
                                    <col>
                                    <col>
                                </colgroup>

                                <thead>
                                <tr>
                                    <th class="text-center background-dark">
                                        <i class="material-icons md-20 table-icon contrast-primary-light" aria-hidden="true" role="img">work_history</i>
                                    </th>
                                    <table-search sort-field="title" v-model="search.title" text="Kontakt"/>
                                    <th v-sort="'time_gross_int'">Zeit</th>
                                </tr>
                                </thead>

                                <tbody>
                                <tr v-for="project in listEntries" class="vertical-align-top" :class="{'text-danger thick-border-top thick-border-bottom': !project.id}">
                                    <td></td>

                                    <td class="text-overflow">
                                        <router-link v-if="project.id && !project.is_deleted" :to="{name: 'time-tracker-project-view', params: {id: project.id}}">
                                            {{project.title}}
                                        </router-link>
                                        <span v-else>{{project.title}}</span>
                                    </td>

                                    <td :title="contactTimesTotal.break.int ? formatSeconds(project.time_net.int) + ' - ' + project.break.string + ' Pause' : null">
                                        {{project.time_gross.string}} Stunden
                                        <small v-if="project.break.int" title="Automatischer Pausenabzug">(abzgl. {{project.break.string}} Pause)</small>
                                    </td>
                                </tr>

                                <tr class="thick-border-top font-weight-500">
                                    <td></td>

                                    <td>
                                        <div class="spacer-10 spacer-10-bottom">
                                            Summe Kontakt Zeiten
                                        </div>
                                    </td>

                                    <td :title="contactTimesTotal.break.int ? formatSeconds(projectTimesTotal.time_net.int) + ' - ' + projectTimesTotal.break.string + ' Pause' : null">
                                        <div class="spacer-10 spacer-10-bottom">
                                            {{contactTimesTotal.time_gross.string}} Stunden
                                            <small v-if="projectTimesTotal.break.int" title="Automatischer Pausenabzug">(abzgl. {{projectTimesTotal.break.string}} Pause)</small>
                                        </div>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </list-pager>

                    </div>
                    <div class="col-lg-6">
                        <portuna-box id="notes-box" title="Notizen" title-icon="receipt" class="shaded">
                            <note-editor ref="notes" :notes="notes" :editor-config="editorConfig" :add-note-callback="addNote" hide-empty/>
                        </portuna-box>
                    </div>
                </div>
            </template>
        </template>
        <template v-if="!tableOnly && isManualMode && weekEntry && weekEntry.id" #sidebar>
            <portuna-box title="Weitere Informationen" title-icon="info" title-type="light" class="spacer-30">
                <strong class="color-contrast-primary-light">Zuletzt bearbeitet</strong>
                <entry-history :history="weekEntry" class="spacer-15"/>
            </portuna-box>
        </template>
    </component>
</template>

<script lang="ts">
    import AppPopover from '@/components/AppPopover.vue';
    import AppPopup from '@/components/AppPopup.vue';
    import {parseFormData} from '@/library/form';
    import {FroalaToolbarRegular} from '@/library/froala';
    import Loader from '@/library/intranet/loader';
    import {INSIGHT_ATTRIBUTES} from '@/library/modules/employee';
    import TopMessageHandler from '@/library/topMessageHandler';
    import {SearchAbstract} from '@/mixins/SearchAbstract';
    import store from '@/store';
    import AbsenceViewPopup from '@/views/absences/AbsenceViewPopup.vue';
    import FreezeViewPopup from '@/views/time-tracker/overtime/FreezeViewPopup.vue';
    import RequestViewPopup from '@/views/time-tracker/overtime/RequestViewPopup.vue';
    import VacationEntryViewPopup from '@/views/vacation/entry/VacationEntryViewPopup.vue';
    import axios from 'axios';
    import {markRaw} from 'vue';
    import {Component, mixins, Prop, Watch} from 'vue-facing-decorator';
    import {Action, Getter} from 'vuex-facing-decorator';
    import AppContent from '../../../components/AppContent.vue';
    import EntryHistory from '../../../components/EntryHistory.vue';
    import FormInputDate from '../../../components/form/FormInputDate.vue';
    import FormSelect from '../../../components/form/FormSelect.vue';
    import ListPager from '../../../components/ListPager.vue';
    import LoadingIndicator from '../../../components/LoadingIndicator.vue';
    import NoteEditor from '../../../components/NoteEditor.vue';
    import PersonBoxEmployee from '../../../components/PersonBoxEmployee.vue';
    import PortunaBox from '../../../components/PortunaBox.vue';
    import TableSearch from '../../../components/TableSearch.vue';
    import formatSeconds from '../../../filters/formatSeconds';
    import {getDateInformation, getDayName} from '../../../library/date';
    import {getEmployeeBasicBreadcrumb} from '../../../library/modules/employee/employee';
    import AEvaluationTableComponent from '../../../library/modules/time-tracker/AEvaluationTableComponent';
    import {requestCorrection} from '../../../library/modules/time-tracker/correction';
    import {EVALUATION_LEGEND, getMonday} from '../../../library/modules/time-tracker/evaluation';
    import {
        getWorkDayHoursBySettings,
        getWorkDaysAndHoursStringByWorkDays,
        NOTE_WEEK,
        PATROL_PROJECT,
        saveTimeTrackerNote,
        TIMETRACKER_SYSTEM_ICONS,
    } from '../../../library/modules/time-tracker/time-tracker';
    import {Popup} from '../../../library/popup';
    import {filterObject} from '../../../library/utils';
    import {errorHandler} from '../../../utils/portuna';
    import dateHelper from '../../../utils/portuna/dateHelper';
    import EvaluationAttendances from './EvaluationAttendances.vue';
    import EvaluationDayNote from './EvaluationDayNote.vue';

    @Component({
        name: 'EvaluationView',
        components: {
            AppPopover,
            TableSearch,
            ListPager,
            EntryHistory,
            FormSelect,
            EvaluationAttendances,
            AppContent,
            FormInputDate,
            LoadingIndicator,
            NoteEditor,
            PersonBoxEmployee,
            PortunaBox,
        },
        methods: {
            dateHelper,
            formatSeconds,
            requestCorrection,
        },
    })
    export default class EvaluationView extends mixins(AEvaluationTableComponent, SearchAbstract)
    {
        readonly $refs: { noteForm: HTMLFormElement };

        @Getter('employeeConfiguration') employeeConfiguration: EmployeeConfiguration;

        @Action('createPopup') createPopup: (options) => Promise<AppPopup>;

        @Getter('employeeConfiguration') employeeConfig: EmployeeConfiguration;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) tableOnly: boolean;

        patrolProject = PATROL_PROJECT;

        accessibleEmployeesSelectOptions: SelectOption[] = [];

        breadcrumb: Breadcrumb[] = [
            getEmployeeBasicBreadcrumb(),
            {
                label: 'Zeiterfassung',
                href: {name: 'time-tracker'},
            },
            {
                label: 'Wochenansicht',
            },
        ];

        editorConfig: Partial<FroalaOptions> = {
            toolbarButtons: FroalaToolbarRegular(),
            heightMin: 150,
        };

        days: Record<string, EvaluationDayEntry> = {};

        workTimeWidget: any = {};

        requestedCorrections: Record<number, EvaluationRequestedCorrection> = {};

        note: string = '';

        notes: NoteEntry[] = [];

        hasProject: boolean = false;

        projectTimes: ({id: number; title: string; time_gross_int: number} | TimeDetails)[] = [];

        projectTimesTotal = {} as TimeDetails;

        projectTimesDate: Record<string, any> = {};

        hasContact: boolean = false;

        contactTimes: ({ id: number; title: string; time_gross_int: number } | TimeDetails)[] = [];

        contactTimesTotal = {} as TimeDetails;

        contactTimesDate: Record<string, any> = {};

        modeClockEnabled: boolean = true;

        modeManualEnabled: boolean = true;

        calculateNonWorkdays: boolean = false;

        calculateBeforeWorkStart: boolean = false;

        calculateAfterWorkEnd: boolean = false;

        allowWorkOutsideWorkTime: boolean = false;

        options: Record<string, string> = {
            week: this.week,
        };

        errorMessage: Record<string, string> = {};

        freezeEnd: Nullable<IOvertimeFreeze> = null;

        allowManualOvertimeFreeze: boolean = false;

        allowedEdit: boolean = false;

        text: string ='Sie können für diese Woche keine Korrekturen mehr beantragen, da für diese Woche die Auswertung geprüft und festgesetzt wurde.';

        coreTimeErrors = {
            redm: {
                type: 'error',
                icon: 'clear',
                message: 'Die Mindestpausenzeit wurde nicht erreicht!',
            },
            redp: {
                type: 'error',
                icon: 'clear',
                message: 'Eine Pause fand außerhalb der Pausenzeiten statt!',
            },
            redl: {
                type: 'error',
                icon: 'clear',
                message: 'Eine Pause hat die maximale Pausenlänge überschritten!',
            },
            work_session_violation: {
                type: 'error',
                icon: 'clear',
                message: 'Fehlende Ruhepause nach einer langen Arbeitssitzung!',
            },
            noBirthday: {
                type: 'warning',
                icon: 'info',
                message: 'Dieser Mitarbeiter besitzt keinen eingetragenen Geburtstag. Der Geburtstag wird zur Berechnung der Mindestpausenzeit sowie bei der Überprüfung auf Verstöße benötigt!',
            },
            inTolerance: {
                type: 'warning',
                icon: 'clear',
                message: 'Eine Pause hat die maximale Pausenlänge überschritten, die Pausenlänge befindet sich allerdings noch in der Toleranz!',
            },
        };

        timeTrackerCheckViolations = false;

        overtimeAbsences: Record<string, {duration: number}> = {};

        weekEntry: Nullable<EvaluationWeekEntry> = null;

        systemIcon = TIMETRACKER_SYSTEM_ICONS;

        get workDays()
        {
            return getWorkDayHoursBySettings(this.settings);
        }

        get workDaysList()
        {
            return getWorkDaysAndHoursStringByWorkDays(this.workDays);
        }

        get daysFiltered()
        {
            return filterObject(this.days, ({key}) => this.checkKey(key));
        }

        get usedSystems()
        {
            let systems = {};

            for (const day of Object.keys(this.daysFiltered))
            {
                const dayData = this.daysFiltered[day];

                for (const dayEntries of [dayData.data, dayData.pause.data])
                {
                    for (const entry of dayEntries)
                    {
                        if (entry.beginSystem)
                        {
                            systems[entry.beginSystem] = 1;
                        }

                        if (entry.endSystem)
                        {
                            systems[entry.endSystem] = 1;
                        }
                    }
                }
            }

            return Object.keys(systems);
        }

        get hasSurcharges()
        {
            return [...Object.values(this.daysFiltered)].some(day => day.surcharges.string !== '00:00' && day.surcharges.string !== '');
        }

        get appContentProperties()
        {
            if (this.tableOnly)
            {
                return {};
            }

            return {
                headline: 'Zeiterfassung: Wochenansicht',
                breadcrumb: this.breadcrumb,
            };
        }

        get evaluationLegend()
        {
            const legend = [];

            for (const entry of EVALUATION_LEGEND)
            {
                if (!this.timeTrackerCheckViolations && [1, 2].includes(EVALUATION_LEGEND.indexOf(entry)))
                {
                    continue;
                }

                legend.push(entry);
            }

            return legend;
        }

        get hasMode()
        {
            return this.isManualMode || this.isTimeClockMode;
        }

        get isManualMode()
        {
            return this.modeManualEnabled;
        }

        get isTimeClockMode()
        {
            return this.modeClockEnabled;
        }

        get editIconLabel()
        {
            return this.isTimeClockMode ? 'Korrektur beantragen' : 'Zeiten eintragen';
        }

        get searchStorageScope()
        {
            return 'time-tracker-correction-project-list';
        }

        get insightAttributes()
        {
            return this.employeeConfig.version ? {} : INSIGHT_ATTRIBUTES;
        }

        @Watch('options.week')
        watchWeek()
        {
            window.setTimeout(() => this.optionsChange());
        }

        @Watch('$props', {deep: true})
        fetch()
        {
            this.tableOnly ? this.fetchTable() : this.fetchView();
        }

        created()
        {
            this.fetch();
        }

        fetchView()
        {
            this.dateInformation = getDateInformation(this.week);
            return this.$store.dispatch('getPage', {url: `/employee/time-tracker/evaluation/fetch-view/employee/${this.employee}/week/${this.week}`})
            .then(({data: {data: r}}) =>
                {
                    this.days                             = r.listData;
                    this.workTimeWidget                   = r.totalWorkTimeWidget;
                    this.requestedCorrections             = r.requestedCorrections || {};
                    this.notes                            = r.notes;
                    this.errorMessage                     = r.errorMessage;
                    this.timeTrackerCheckViolations       = r.timeTrackerCheckViolations;
                    this.overtimeAbsences                 = r.overtime_absences;
                    this.accessibleEmployeesSelectOptions = r.accessible_employees_select_options;
                    this.weekEntry                        = r.week_entry;
                    this.hasProject                       = r.project_enabled;
                    this.projectTimes                     = r.project_times;
                    this.projectTimesTotal                = r.project_times_total;
                    this.projectTimesDate                 = r.project_times_date;
                    this.hasContact                       = r.contact_enabled;
                    this.contactTimes                     = r.contact_times;
                    this.contactTimesTotal                = r.contact_times_total;
                    this.contactTimesDate                 = r.contact_times_date;
                    this.modeClockEnabled                 = r.mode_clock_enabled;
                    this.modeManualEnabled                = r.mode_manual_enabled;
                    this.startDate                        = r.start_date;
                    this.workStart                        = r.work_start;
                    this.employeeEnabledSystems           = r.systems;
                    this.settings                         = r.settings;
                    this.freezeEnd                        = r.freeze_end;
                    this.allowManualOvertimeFreeze        = r.allow_manual_overtime_freeze;
                    this.allowedEdit                      = r.allowed_edit;
                    this.allowWorkOutsideWorkTime         = r.allow_work_outside_work_time_current;
                    this.calculateNonWorkdays             = r.calculate_non_workdays;
                    this.calculateBeforeWorkStart         = r.calculate_before_work_start;
                    this.calculateAfterWorkEnd            = r.calculate_after_work_end;
                },
            )
            .catch(error =>
            {
                console.error(error);
                errorHandler(error);
                this.routerGoBack({name: 'time-tracker-evaluation'});
            });
        }

        reloading = false;

        reloadData()
        {
            this.reloading = true;
            return this.$store.dispatch(
                'getPage',
                {
                    url: `/employee/time-tracker/evaluation/fetch-view/employee/${this.employee}/week/${this.week}`,
                    setLoading: false,
                },
            )
            .then(({data: {data: r}}) =>
                {
                    this.days                 = r.listData;
                    this.workTimeWidget       = r.totalWorkTimeWidget;
                    this.requestedCorrections = r.requestedCorrections || {};
                },
            )
            .catch(error =>
            {
                console.error(error);
                errorHandler(error);
                this.routerGoBack({name: 'time-tracker-evaluation'});
            }).finally(() =>
                {
                    this.reloading = false;
                },
            );
        }

        fetchTable()
        {
            this.dateInformation = getDateInformation(this.week);
            return this.axios.get(`/employee/time-tracker/evaluation/fetch-view/employee/${this.employee}/week/${this.week}/table/1`)
            .then(({data: {data: r}}) =>
                {
                    this.days                   = r.listData;
                    this.employeeEnabledSystems = r.systems;
                    this.errorMessage           = r.errorMessage;
                },
            )
            .catch((err) =>
            {
                errorHandler(err);
            });
        }

        checkKey(key: string)
        {
            return key !== 'allocatedWorkTime' && key !== 'calc' && key !== 'raw' && key !== 'color';
        }

        addNote(formData: { note: string })
        {
            saveTimeTrackerNote(formData, 0, this.employee, this.week, NOTE_WEEK).then(entry =>
            {
                this.note = '';
                this.notes.unshift(entry);
            });
        }

        editDayNote(day: string, dayEntry: EvaluationDayEntry): Popup
        {
            const note = dayEntry.note;

            let popup = new Popup({
                title: note ? 'Kommentar bearbeiten' : 'Kommentar hinzufügen',
                size: 'medium',
                titleIcon: 'subject',
                component: EvaluationDayNote,
                componentProps: {
                    employee: this.employee,
                    day,
                    dayEntry,
                    overtimeAbsences: this.overtimeAbsences,
                    requestedCorrections: this.requestedCorrections,
                    settings: this.settings,
                },
                events: {
                    saved: (note: NoteEntry) => dayEntry['note'] = note,
                },
            });

            popup.open();

            return popup;
        }

        optionsChange()
        {
            let employee = String(this.employee),
                week     = getMonday(new Date(this.options.week));

            this.$router.push({
                name: 'time-tracker-evaluation-view',
                params: {
                    employee,
                    week,
                },
            });

            window.setTimeout(() => this.fetch());
        }

        checkRequestCorrection(date: string, employee: number): void
        {
            if (this.isManualMode)
            {
                this.$router.push({
                    name: 'time-tracker-evaluation-edit',
                    params: {
                        employee: String(this.employee),
                        week: this.week,
                    },
                });
                return;
            }

            requestCorrection(employee, date, {'send-request': () => {
                this.fetchView();
                this.reloadData();
            }});
        }

        isWorkDay(date: string)
        {
            return !!this.getWorkTimes(date);
        }

        getWorkTimes(date: string)
        {
            return this.workDays[getDayName(date)];
        }

        showRequestPopup(id: number, type: string, absenceId: boolean = false)
        {
            if ('time_tracker_overtime_cut' === type)
            {
                let data = {};

                if (absenceId)
                {
                    data.absenceId = id;
                }
                else
                {
                    data.id = id;
                }

                store.dispatch('createPopup', {
                    title: 'Stundenantrag anzeigen',
                    titleIcon: 'access_time',
                    size: 'small',
                    component: markRaw(RequestViewPopup),
                    componentProps: data,
                }).then((popup: AppPopup) =>
                {
                    popup.open();
                });

                return;
            }
            else if ('vacation' === type)
            {
                store.dispatch('createPopup', {
                    title: 'Urlaubsantrag',
                    titleIcon: 'flight_takeoff',
                    size: 'medium',
                    component: markRaw(VacationEntryViewPopup),
                    componentProps: {id},
                }).then((popup: AppPopup) =>
                {
                    popup.open();
                });

                return;
            }
            else if ('absence' === type)
            {
                const popup = new Popup({
                    title: 'Abwesenheit',
                    titleIcon: 'block',
                    size: 'small',
                    component: AbsenceViewPopup,
                    componentProps: {
                        id,
                        employeeId: this.employee,
                    },
                });

                popup.open();

                return;
            }
        }

        showFreezeInfoPopup()
        {
            if (!this.allowManualOvertimeFreeze)
            {
                return;
            }

            this.$store.dispatch('createPopup', {
                title: 'Fixierter Stundenkonto-Wert',
                titleIcon: 'lock',
                size: 'small',
                component: FreezeViewPopup,
                componentProps: {
                    freezeEnd: this.freezeEnd,
                    text: 'Sie können für diese Woche keine Korrekturen mehr beantragen, da für diese Woche die Auswertung geprüft und festgesetzt wurde.,'
                },
            } as PopupOptions).then((popup: AppPopup) =>
            {
                popup.open();
            });
        }
    }
</script>